import React, { useCallback } from 'react';
import { APP_ROUTES } from '@/navigators/routes';
import { AppStackParams } from '@/navigators/types';
import { NavigateToMicroproductPayload } from '@domain/microproducts/components/micro-product-webview-types';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { StorefrontMicroProductView } from '@/screens/storefront-microproducts/storefront-micro-product-view';

/**
 * In-container surface for storefront microproducts: one hidden tab in
 * app-tabs, sibling to the Links/ExternalContentWrapper tab. The nav rail
 * stays visible and content swaps in place — no pushed screen, no back
 * button. Hidden from the rail the same way Links is (the More popup is the
 * only entry); which product renders travels by route params. Param
 * survival across navigator remounts (restaurant switch) comes from the
 * registration's one-shot initialParams seed in app-tabs — never from a
 * live prop, which would go stale and blank the kept-alive WebView.
 */
export const StorefrontMicroProductTab: React.FC = () => {
  const navigation = useNavigation<StackNavigationProp<AppStackParams>>();
  const route =
    useRoute<RouteProp<AppStackParams, APP_ROUTES.StorefrontMicroProduct>>();
  const productId = route.params?.productId;

  // SPA-initiated cross-microproduct navigation: stay on this tab, swap the
  // product via params.
  const handleNavigateToMicroproduct = useCallback(
    (payload: NavigateToMicroproductPayload) => {
      navigation.navigate(APP_ROUTES.StorefrontMicroProduct, {
        productId: payload.productId,
      });
    },
    [navigation],
  );

  return (
    <StorefrontMicroProductView
      onNavigateToMicroproduct={handleNavigateToMicroproduct}
      productId={productId}
    />
  );
};
